import { validMackAcquisitionReceipt } from './mackAcquisitionView';
import type { Message } from '../components/ChatWindow';

export interface MackSyntheticStatus {
  schema: 'mack_synthetic_status_v1'; domain: 'TRADING'; agent: 'MACK';
  semantic_address: 'calendar.calendar'; worker_identity: 'trading.mack.claude';
  request_id: string; job_id: string; trace_id: string;
  state: 'SYNTHETIC_VALIDATED_NO_OPERATIONAL_TRUTH'; synthetic: true;
  operational_truth: false; production_admission: false;
  prompt_sha256: string; capture_sha256: string; response_sha256: string;
  status_sha256: string; readback: 'PASS';
}

export interface MackTurn {
  domain: 'TRADING';
  agent: 'MACK';
  request_id: string;
  trace_id?: string;
  current_turn_response: boolean;
  synthetic_status?: MackSyntheticStatus;
}

export function isMackTextTurn(domain: unknown, agent: unknown): boolean {
  return String(domain).toUpperCase() === 'TRADING' && String(agent).toUpperCase() === 'MACK';
}

export function newMackRequestId(): string {
  // getRandomValues also works on the existing HTTP LAN interface.
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return `mack_${Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')}`;
}

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown> : null;
}

export function mackCurrentResponse(payload: unknown, requestId: string): Message | null {
  const data = record(payload);
  const audit = record(record(data?.facts)?.mack_semantic_dual_run);
  if (!data || !audit || data.success !== true || (data.memory_saved !== false && data.memory_saved !== true)
    || data.current_turn_response !== true || data.source !== 'MACK_SEMANTIC_SAFETY_GATE'
    || !isMackTextTurn(data.domain, data.agent)
    || audit.mack_final_output_validator_invoked !== true
    || data.request_id !== requestId || audit.request_id !== requestId
    || typeof data.trace_id !== 'string' || !data.trace_id.trim()
    || audit.trace_id !== data.trace_id
    || typeof data.response !== 'string' || !data.response.trim()
    || data.agent_response !== data.response) return null;

  const saved = data.memory_saved === true;
  const synthetic = record(data.synthetic_status_receipt);
  const acquisition = record(data.acquisition);
  if (synthetic || acquisition?.synthetic === true) {
    const job = record(acquisition?.job), capture = record(job?.capture_binding);
    if (!synthetic || saved || data.acquisition_receipt != null
      || synthetic.schema !== 'mack_synthetic_status_v1' || synthetic.synthetic !== true
      || synthetic.operational_truth !== false || synthetic.production_admission !== false
      || synthetic.state !== 'SYNTHETIC_VALIDATED_NO_OPERATIONAL_TRUTH'
      || acquisition?.state !== synthetic.state || acquisition?.production_admission !== false
      || synthetic.readback !== 'PASS' || synthetic.domain !== 'TRADING' || synthetic.agent !== 'MACK'
      || synthetic.semantic_address !== 'calendar.calendar'
      || data.selected_semantic_address !== synthetic.semantic_address
      || synthetic.worker_identity !== 'trading.mack.claude'
      || job?.worker_identity !== synthetic.worker_identity
      || synthetic.request_id !== requestId || synthetic.trace_id !== data.trace_id
      || synthetic.job_id !== data.job_id || job?.job_id !== synthetic.job_id
      || synthetic.prompt_sha256 !== job?.prompt_sha256
      || synthetic.capture_sha256 !== capture?.capture_sha256
      || synthetic.response_sha256 !== acquisition?.candidate_sha256
      || !['prompt_sha256','capture_sha256','response_sha256','status_sha256'].every(key =>
        typeof synthetic[key] === 'string' && /^[a-f0-9]{64}$/.test(synthetic[key] as string))) return null;
  }
  if (saved && !validMackAcquisitionReceipt(data.acquisition_receipt, requestId,
    data.trace_id as string, data.selected_semantic_address, data.job_id)) return null;
  if (saved && typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('mack-acquisition-updated', {
    detail: { domain: 'TRADING', agent: 'MACK', semantic_address: data.selected_semantic_address },
  }));

  return {
    id: `mack_response_${requestId}`,
    sender: 'rexy',
    text: data.response,
    timestamp: new Date().toLocaleTimeString(),
    source: data.source,
    memoryStatus: saved ? 'saved' : null,
    mackTurn: {
      domain: 'TRADING', agent: 'MACK', request_id: requestId,
      trace_id: data.trace_id, current_turn_response: true,
      ...(synthetic ? { synthetic_status: synthetic as unknown as MackSyntheticStatus } : {}),
    },
  };
}

export function mergeMackHistory(
  rows: Array<{ role: string; content: string; source?: string; memoryStatus?: Message['memoryStatus'] }>,
  existing: Message[],
  senderForRole: (role: string) => Message['sender'],
): Message[] {
  const history: Message[] = rows.filter(row => typeof row.content === 'string').map((row, i) => ({
    id: `restored_mack_${i}`, sender: senderForRole(row.role), text: row.content,
    timestamp: '', source: row.source || 'LOCAL_AI', memoryStatus: row.memoryStatus || null,
  }));
  // History has no authority to replace, deduplicate or relabel ephemeral turns.
  const current = existing.filter(message => isMackTextTurn(message.mackTurn?.domain, message.mackTurn?.agent));
  return [...history, ...current];
}
